"use client";

import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { TKey } from "@/app/translations/TKey";
import { useT } from "@/app/translations/useT";
import { BlogArticle } from "@/app/types/BlogArticle";
import { Limits } from "@/app/vars/Limits";
import { getCategoryLabelKey } from "../BlogCategories/BlogCategories.logic";
import BlogCover from "../BlogCover/BlogCover";
import styles from "./BlogArticleCard.module.css";
import { articleSeed, formatCardDate, getArticleLocale } from "./BlogArticles.logic";

type Props = {
  article: BlogArticle;
  lang: string;
};

export default function BlogArticleCard(props: Props) {
  const T = useT();
  const { article, lang } = props;
  const categoryKey = getCategoryLabelKey(article.categoryId);
  const locale = getArticleLocale(article, lang);

  const [isWanted, setIsWanted] = useState(false);
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelHover = () => {
    if (hoverTimer.current) {
      clearTimeout(hoverTimer.current);
      hoverTimer.current = null;
    }
  };

  const startHover = () => {
    if (isWanted) {
      return;
    }
    cancelHover();
    hoverTimer.current = setTimeout(() => setIsWanted(true), Limits.HOVER_PREFETCH_DELAY);
  };

  useEffect(() => {
    return () => {
      if (hoverTimer.current) {
        clearTimeout(hoverTimer.current);
      }
    };
  }, []);

  return (
    <Link
      href={`/${lang}/blog/${article.slug[lang]}`}
      className={styles.card}
      prefetch={isWanted || undefined}
      onMouseEnter={startHover}
      onMouseLeave={cancelHover}
      onFocus={() => setIsWanted(true)}
    >
      <BlogCover categoryId={article.categoryId} seed={articleSeed(article)} />

      <div className={styles.body}>
        <div className={styles.meta}>
          {categoryKey && <span className={styles.category}>{T(categoryKey)}</span>}
          <span className={styles.readTime}>{`${article.readTime} min`}</span>
        </div>
        <h2 className={styles.title}>{locale.title}</h2>
        <p className={styles.excerpt}>{locale.excerpt}</p>
        <div className={styles.footer}>
          <span>{formatCardDate(article.date, lang)}</span>
          <span className={styles.readMore}>
            {T(TKey.BLOG_READ_ARTICLE)}
            <span className={styles.arrow}>→</span>
          </span>
        </div>
      </div>
    </Link>
  );
}
